import {
    Workflow,
    LineChart,
    Brain,
    MessageSquare,
    Zap,
    Cloud,
    ArrowUpRight,
} from "lucide-react";

const AISolutions = () => {
    const solutions = [
        {
            Icon: Workflow,
            number: "01",
            title: "AI Automation Platforms",
            description:
                "Workflow orchestration, task automation, and human-in-the-loop approvals that plug into your existing ERP, CRM, and RPA stack.",
        },
        {
            Icon: LineChart,
            number: "02",
            title: "Data Analytics & AI Insights",
            description:
                "Dashboards, KPI frameworks, predictive analytics, and anomaly detection built on top of your operational data.",
        },
        {
            Icon: Brain,
            number: "03",
            title: "Custom AI/ML Models",
            description:
                "From proof-of-concept to production — bespoke models trained on your data, deployed on AWS, Azure, or GCP.",
        },
        {
            Icon: MessageSquare,
            number: "04",
            title: "Conversational AI",
            description:
                "GPT-powered assistants for customer support, internal knowledge, and workflow triage — with memory and tool-use.",
        },
        {
            Icon: Zap,
            number: "05",
            title: "Process Automation",
            description:
                "End-to-end business process automation, from data capture to compliance — reducing cycle times and human error.",
        },
        {
            Icon: Cloud,
            number: "06",
            title: "Cloud AI Infrastructure",
            description:
                "Scalable, secure ML infrastructure with FERPA / SOC 2 posture. Multi-cloud, cost-optimized, production-hardened.",
        },
    ];

    return (
        <section id="solutions" className="relative py-32 border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-4">
                            / Solutions
                        </div>
                        <h2
                            className="text-5xl md:text-6xl lg:text-7xl font-display leading-[0.95] tracking-tight text-white max-w-3xl"
                            data-testid="solutions-heading"
                        >
                            Six practice areas.
                            <br />
                            <em className="italic text-white/50">One outcome —</em>
                            <br />
                            operational leverage.
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md text-[15px] leading-relaxed">
                        We don&apos;t sell tools. We ship measurable outcomes — every engagement
                        is scoped against ROI, not activity.
                    </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10">
                    {solutions.map((s, i) => (
                        <button
                            key={i}
                            onClick={() =>
                                document
                                    .getElementById("contact")
                                    ?.scrollIntoView({ behavior: "smooth" })
                            }
                            className="group text-left bg-ink-950 hover:bg-ink-900 p-8 md:p-10 transition-all duration-500 relative overflow-hidden"
                            data-testid={`solution-card-${i}`}
                        >
                            <div className="flex items-start justify-between mb-8">
                                <div className="w-11 h-11 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:bg-accent-brand group-hover:border-accent-brand transition-all duration-300">
                                    <s.Icon
                                        className="w-5 h-5 text-white/70 group-hover:text-ink-950 transition-colors"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <span className="text-[11px] font-mono text-white/30 tracking-widest">
                                    {s.number}
                                </span>
                            </div>
                            <h3 className="text-2xl font-display text-white mb-3 tracking-tight">
                                {s.title}
                            </h3>
                            <p className="text-white/50 text-[14px] leading-relaxed mb-6">
                                {s.description}
                            </p>
                            <div className="flex items-center gap-2 text-accent-brand text-[12px] font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                Discuss project
                                <ArrowUpRight className="w-3.5 h-3.5" />
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AISolutions;
